import { SupabaseClient } from "@supabase/supabase-js";
import dayjs from "@punchup/utils/dayjs";
import { Database } from "../../supabase.types";
import { logException, logSupabaseException } from "@punchup/sentry";
import type { CreateSpecialRequest } from "../../models/specials";
import { addSpecialToCollection } from "./collections";

type SpecialRow = Database["public"]["Tables"]["specials"]["Row"];

/**
 * Atomically publishes the special if all conditions are met (title set, not
 * future-dated, pipeline complete) and returns whether it went live. Returns
 * the raw { published, error } so each caller decides whether an RPC error is
 * fatal — create treats it as non-fatal (the cron retries), the pipeline-
 * complete handler surfaces it as an "error" outcome.
 */
export async function tryPublishSpecialOnCreate(
  supabase: SupabaseClient<Database>,
  specialId: string
): Promise<{ published: boolean | null; error: { message: string } | null }> {
  const { data, error } = await supabase.rpc("try_publish_special_on_create", {
    p_special_id: specialId,
  });
  return { published: data, error };
}

export async function updateSpecial(
  supabase: SupabaseClient<Database>,
  specialId: string,
  comedianId: string,
  updates: {
    description?: string | null;
    preview_img?: string | null;
    small_preview_img?: string | null;
    requires_subscription?: boolean;
    requires_purchase?: boolean;
    subscription_product_id?: string | null;
    required_subscription_tier?: number | null;
  }
): Promise<SpecialRow> {
  const payload: Database["public"]["Tables"]["specials"]["Update"] = {};
  if ("description" in updates) payload.description = updates.description;
  if ("preview_img" in updates) payload.preview_img = updates.preview_img;
  if ("small_preview_img" in updates)
    payload.small_preview_img = updates.small_preview_img;
  if (updates.requires_subscription != null)
    payload.requires_subscription = updates.requires_subscription;
  if (updates.requires_purchase != null)
    payload.requires_purchase = updates.requires_purchase;
  if ("subscription_product_id" in updates)
    payload.subscription_product_id = updates.subscription_product_id;
  if ("required_subscription_tier" in updates)
    payload.required_subscription_tier = updates.required_subscription_tier;

  const { data, error } = await supabase
    .from("specials")
    .update(payload)
    .eq("id", specialId)
    .eq("comedian_id", comedianId)
    .select()
    .single();

  if (error) {
    logSupabaseException(error, `Failed to update special ${specialId}`);
    throw error;
  }

  return data;
}

export async function createSpecialForGcpVideo(
  supabase: SupabaseClient<Database>,
  input: CreateSpecialRequest & { required_subscription_tier?: number | null }
): Promise<{ id: string; published: boolean; created_at: string | null }> {
  const insert: Database["public"]["Tables"]["specials"]["Insert"] = {
    comedian_id: input.comedian_id,
    title: input.title,
    slug: input.slug,
    punchup_video_id: input.punchup_video_id,
    is_live: false,
    ...(input.preview_img != null && { preview_img: input.preview_img }),
    ...(input.small_preview_img != null && {
      small_preview_img: input.small_preview_img,
    }),
    ...(input.description != null && { description: input.description }),
    ...(input.requires_subscription != null && {
      requires_subscription: input.requires_subscription,
    }),
    ...(input.requires_purchase != null && {
      requires_purchase: input.requires_purchase,
    }),
    ...(input.subscription_product_id != null && {
      subscription_product_id: input.subscription_product_id,
    }),
    ...(input.required_subscription_tier != null && {
      required_subscription_tier: input.required_subscription_tier,
    }),
    ...(input.runtime != null && { runtime: input.runtime }),
    ...(input.is_audio != null && { is_audio: input.is_audio }),
    ...(input.published_at != null && { published_at: input.published_at }),
  };

  const { data, error } = await supabase
    .from("specials")
    .insert(insert)
    .select("id, created_at")
    .single();

  if (error) {
    logSupabaseException(error, "Failed to create special for GCP video");
    throw error;
  }

  // Link the special to a collection if the upload targeted one. The legacy
  // comedian_video_uploads flow carried collection_id on the upload row; the GCP
  // create path associates it here. Best-effort — a failure to associate must not
  // fail special creation.
  if (input.collection_id != null) {
    try {
      await addSpecialToCollection({
        collection_id: input.collection_id,
        special_id: data.id,
        comedian_id: input.comedian_id,
      });
    } catch (collectionError) {
      logException(collectionError, {
        data: `Failed to add special ${data.id} to collection ${input.collection_id}`,
      });
    }
  }

  // Stamp the opaque product-metadata bag onto the linked video. The pipeline
  // carries it untouched and hands it back at completion; the specials surface
  // reads `make_featured` from it to feature the special at publish. Non-fatal:
  // the special is created regardless — a failure just means surface metadata
  // (e.g. featuring) won't apply.
  if (input.product_metadata != null) {
    const { error: metaError } = await supabase
      .from("videos")
      .update({
        product_metadata:
          input.product_metadata as unknown as Database["public"]["Tables"]["videos"]["Update"]["product_metadata"],
      })
      .eq("id", input.punchup_video_id);

    if (metaError) {
      logSupabaseException(
        metaError,
        `Failed to stamp product_metadata on video ${input.punchup_video_id}`
      );
    }
  }

  const { published: rpcData, error: rpcError } =
    await tryPublishSpecialOnCreate(supabase, data.id);

  if (rpcError) {
    logSupabaseException(
      { message: rpcError.message, details: "", hint: "", code: "" },
      `try_publish_special_on_create failed for special ${data.id}`
    );
    // Non-fatal — the special is created; Trigger 1 will publish when pipeline completes.
  }

  return {
    id: data.id,
    published: rpcData === true,
    created_at: data.created_at,
  };
}

export type GcpPipelineSpecial = {
  id: string;
  comedian_id: string;
  title: string;
  slug: string;
  preview_img: string | null;
  requires_subscription: boolean;
  published_at: string | null;
  created_at: string | null;
};

export async function getSpecialByPunchupVideoId(
  supabase: SupabaseClient<Database>,
  punchupVideoId: string
): Promise<GcpPipelineSpecial | null> {
  const { data, error } = await supabase
    .from("specials")
    .select(
      "id, comedian_id, title, slug, preview_img, requires_subscription, published_at, created_at"
    )
    .eq("punchup_video_id", punchupVideoId)
    .maybeSingle();

  if (error) {
    logSupabaseException(
      error,
      `Failed to fetch special by punchup_video_id ${punchupVideoId}`
    );
    throw error;
  }

  return data;
}

export type ScheduledGcpSpecial = {
  id: string;
  comedian_id: string;
  title: string;
  slug: string;
  preview_img: string | null;
  requires_subscription: boolean;
  published_at: string | null;
  created_at: string | null;
  // The linked pipeline video, carried so the publish side effects can write
  // back to it (clearing the one-shot make_featured flag).
  punchup_video_id: string;
  // Opaque product-metadata bag from the linked video, carried so the cron
  // publish path can hand it to publishSpecialSideEffects like the other paths.
  product_metadata: Record<string, unknown> | null;
};

/**
 * Returns GCP pipeline specials that are past their scheduled publish date
 * but haven't gone live yet. Called by the cron to handle future-dated
 * publishes that were deferred by both triggers.
 *
 * Also catches any immediate publishes (published_at IS NULL) that slipped
 * through both triggers due to transient errors — belt-and-suspenders.
 *
 * Excludes videos already marked 'published'. `is_live = false` alone does NOT
 * mean "awaiting its first publish" — it is equally the state a manager leaves
 * behind by unpublishing, which writes is_live and nothing else. Without this
 * filter the belt-and-suspenders clause above resurrected deliberately hidden
 * content on the next 5-minute cron tick (PUN-4881). overall_status flips to
 * 'published' on the first successful publish and is never reverted, so it
 * separates the two states the way is_live cannot.
 */
export async function getScheduledGcpPipelineSpecials(
  supabase: SupabaseClient<Database>
): Promise<ScheduledGcpSpecial[]> {
  const now = dayjs().toISOString();

  const { data, error } = await supabase
    .from("specials")
    .select(
      "id, comedian_id, title, slug, preview_img, requires_subscription, published_at, created_at, punchup_video_id, videos!specials_punchup_video_id_fkey!inner(pipeline_complete, overall_status, product_metadata)"
    )
    .not("punchup_video_id", "is", null)
    .eq("is_live", false)
    .eq("videos.pipeline_complete", true)
    .neq("videos.overall_status", "published")
    .or(`published_at.is.null,published_at.lte.${now}`);

  if (error) {
    logSupabaseException(
      error,
      "Failed to fetch scheduled GCP pipeline specials"
    );
    throw error;
  }

  return (data ?? []).map((row) => {
    // The !inner embed resolves to a single related video row; read the opaque
    // bag off it (cast through unknown — PostgREST embed typing is fiddly).
    const video = row.videos as unknown as {
      product_metadata: Record<string, unknown> | null;
    } | null;
    return {
      id: row.id,
      comedian_id: row.comedian_id,
      title: row.title,
      slug: row.slug,
      preview_img: row.preview_img,
      requires_subscription: row.requires_subscription,
      published_at: row.published_at,
      created_at: row.created_at,
      // Non-null by the .not("punchup_video_id", "is", null) filter above.
      punchup_video_id: row.punchup_video_id as string,
      product_metadata: video?.product_metadata ?? null,
    };
  });
}
